import {capitalize} from '@raison/toolbox';
import {InlineSvg, RsnDialog} from '@raison/ui-kit';
import {createSelector} from '@reduxjs/toolkit';
import {format} from 'date-fns/format';
import React, {FC, Fragment, Reducer, useEffect, useMemo, useReducer, useRef} from 'react';
import {useSelector} from 'react-redux';
import {TransactionsHistory} from '~/entities/transactions-history';
import {TransactionIcons} from '~/features/transitions-list';
import {TransactionFilterName} from '~/redux/types/store/Transactions';
import {AccountFilterPopup} from '../TransactionFiltersModals/AccountFilterPopup';
import {DateFilterPopup} from '../TransactionFiltersModals/DateFilterPopup';
import {GroupFilterPopup} from '../TransactionFiltersModals/GroupFilterPopup';
import {MoreFiltersPopup} from '../TransactionFiltersModals/MoreFiltersPupup';
import {StatusFilterPopup} from '../TransactionFiltersModals/StatusFilterPopup';
import scss from './TransactionFilters.module.scss';

const {arrowDownSvg} = TransactionIcons;

type TransactionFilterType = {
    name: TransactionFilterName;
    nameHuman: string;
    activeCount: number;
    defaultOrder: number;
    component: FC;
};

const selectAccounts = (store: AppModel) => store.assets.assets?.accounts;
const accountHumanNamesSelector = () =>
    createSelector(selectAccounts, accounts => {
        return Object.values(accounts || {})
            .flat()
            .map(acc => ({id: acc.id, humanName: acc.name}));
    });

export const TransactionFilters = () => {
    const [pageFilters] = TransactionsHistory.useFilters();
    type popup = FC | null;
    const [PopupComponent, setPopupComponent] = useReducer<Reducer<popup, popup>>((_, next) => next, null);

    const accountHumanNames = useSelector(useMemo(accountHumanNamesSelector, []));

    const clientFilters = useMemo(() => {
        return getClientFilters(pageFilters, accountHumanNames).sort(
            (a, b) => Number(Boolean(b.activeCount)) - Number(Boolean(a.activeCount))
        );
    }, [pageFilters, accountHumanNames]);

    const isSomethingActive = Object.values(clientFilters).some(item => item.activeCount);

    useEffect(() => {
        if (containerRef.current && isSomethingActive) {
            containerRef.current.scrollTo({left: 0});
        }
    }, [isSomethingActive]);

    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <Fragment>
            <div className={scss.filtersWrapper} ref={containerRef}>
                {clientFilters.map(({name, nameHuman, activeCount, component}) => (
                    <div
                        key={name}
                        className={`${scss.filter} ${activeCount ? scss.active : ''}`}
                        onClick={() => setPopupComponent(component)}
                    >
                        {nameHuman}
                        <InlineSvg src={arrowDownSvg} className={scss.arrow} />
                    </div>
                ))}
            </div>

            <RsnDialog.Controller onClose={() => setPopupComponent(null)}>
                {PopupComponent && <PopupComponent />}
            </RsnDialog.Controller>
        </Fragment>
    );
};

const dateRangeHelper = (dateRange: string) => {
    const [from, to] = dateRange.split('_');
    const fromMonth = capitalize(format(from, 'MMM'));
    const fromDay = capitalize(format(from, 'dd'));
    const toMonth = capitalize(format(to, 'MMM'));
    const toDate = capitalize(format(to, 'dd'));
    if (fromMonth === toMonth) {
        if (fromDay === toDate) {
            return `${fromMonth} ${fromDay}`;
        }
        return `${fromMonth} ${fromDay} – ${toDate}`;
    }
    return `${fromMonth} ${fromDay} – ${toMonth} ${toDate}`;
};

function capitalizeFirstLetterOnly(str: string) {
    if (!str) return str;
    return str[0].toUpperCase() + str.slice(1);
}

const formatBadgeLabel = (selectedFiltersCounter: number, firstSelected: string, mainLabel: string) => {
    if (selectedFiltersCounter === 0) {
        return mainLabel;
    }
    if (selectedFiltersCounter === 1) {
        return capitalizeFirstLetterOnly(firstSelected.trim()).replace('_', ' ');
    } else {
        return `${mainLabel} (${selectedFiltersCounter})`;
    }
};

const dateFilterBadgeLabel = (hasSelected: boolean, filterValue: string) => {
    if (hasSelected) {
        return dateRangeHelper(filterValue);
    } else {
        return 'Date';
    }
};

const getClientFilters = (
    filters: TransactionsHistory.Filters,
    accountHumanNames: {id: number; humanName: string}[]
): TransactionFilterType[] => {
    const firstAccountFilter = filters.account?.at(0) ?? filters.wallet?.at(0) ?? filters.token?.at(0);
    const accountFiltersCount =
        (filters.account?.length ?? 0) + (filters.wallet?.length ?? 0) + (filters.token?.length ?? 0);

    const filtersCount = {
        account: accountFiltersCount,
        status: filters.status?.length || 0,
        type: filters.group?.length ?? 0,
        date: filters.date ? 1 : 0,
        more: filters.more?.length ?? 0,
    };

    return [
        {
            name: 'account',
            nameHuman: formatBadgeLabel(
                accountFiltersCount,
                accountHumanNames.find(acc => acc.id === firstAccountFilter)?.humanName || '...',
                'Account'
            ),
            activeCount: filtersCount.account,
            defaultOrder: 1,
            component: AccountFilterPopup,
        },
        {
            name: 'status',
            nameHuman: formatBadgeLabel(filtersCount.status, filters.status?.[0] || '...', 'Status'),
            activeCount: filtersCount.status,
            defaultOrder: 2,
            component: StatusFilterPopup,
        },
        {
            name: 'type',
            nameHuman: formatBadgeLabel(filtersCount.type, filters.group?.[0] || '...', 'Type'),
            activeCount: filtersCount.type,
            defaultOrder: 3,
            component: GroupFilterPopup,
        },
        {
            name: 'date',
            nameHuman: dateFilterBadgeLabel(Boolean(filtersCount.date), filters.date || ''),
            activeCount: filtersCount.date,
            defaultOrder: 4,
            component: DateFilterPopup,
        },
        {
            name: 'more',
            nameHuman: filtersCount.more > 0 ? `More (${filtersCount.more})` : 'More',
            activeCount: filtersCount.more,
            defaultOrder: 5,
            component: MoreFiltersPopup,
        },
    ];
};
